import React, { useEffect, useState } from "react";
import { http } from "@/context/AuthContext";
import { Star, Plus, Trash2, Loader2, ToggleLeft, ToggleRight, MessageSquareQuote } from "lucide-react";
import { SERVICES } from "@/lib/data";

function Stars({ value, onChange }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange?.(n)}
          className={`transition ${n <= value ? "text-gold" : "text-navy/20"} ${onChange ? "hover:scale-110" : "cursor-default"}`}
          data-testid={onChange ? `star-${n}` : undefined}
        >
          <Star className="w-5 h-5 fill-current" />
        </button>
      ))}
    </div>
  );
}

function NewReviewForm({ onSaved }) {
  const [form, setForm] = useState({
    author: "",
    rating: 5,
    body: "",
    service_slug: "",
    location: "",
    active: true,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setErr("");
    try {
      await http.post("/admin/reviews", { ...form, service_slug: form.service_slug || null });
      setForm({ author: "", rating: 5, body: "", service_slug: "", location: "", active: true });
      onSaved?.();
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="card p-6 space-y-4" data-testid="new-review-form">
      <h3 className="text-lg font-serif text-navy">Add a testimonial</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          required
          data-testid="review-author"
          placeholder="Client name (or initials)"
          value={form.author}
          onChange={(e) => setForm({ ...form, author: e.target.value })}
          className="border border-navy/15 rounded-xl px-4 py-2.5 text-sm"
        />
        <input
          data-testid="review-location"
          placeholder="Location (optional, e.g. Indianapolis, IN)"
          value={form.location}
          onChange={(e) => setForm({ ...form, location: e.target.value })}
          className="border border-navy/15 rounded-xl px-4 py-2.5 text-sm"
        />
      </div>
      <div className="flex items-center gap-4">
        <span className="text-xs uppercase tracking-widest text-navy/55">Rating</span>
        <Stars value={form.rating} onChange={(n) => setForm({ ...form, rating: n })} />
      </div>
      <select
        data-testid="review-service"
        value={form.service_slug}
        onChange={(e) => setForm({ ...form, service_slug: e.target.value })}
        className="w-full border border-navy/15 rounded-xl px-4 py-2.5 text-sm bg-white"
      >
        <option value="">Tied to a specific service? (Optional)</option>
        {SERVICES.map((s) => (
          <option key={s.slug} value={s.slug}>{s.name}</option>
        ))}
      </select>
      <textarea
        required
        rows={4}
        data-testid="review-body"
        placeholder="What did the client say?"
        value={form.body}
        onChange={(e) => setForm({ ...form, body: e.target.value })}
        className="w-full border border-navy/15 rounded-xl px-4 py-3 text-sm resize-y"
      />
      {err && <div className="text-red-500 text-xs">{err}</div>}
      <button
        type="submit"
        data-testid="review-submit"
        disabled={saving}
        className="btn-primary disabled:opacity-50"
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        Add Testimonial
      </button>
    </form>
  );
}

export default function AdminReviews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const r = await http.get("/admin/reviews");
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  async function toggle(rev) {
    await http.patch(`/admin/reviews/${rev.review_id}`, { ...rev, active: !rev.active });
    refresh();
  }
  async function remove(rev) {
    if (!window.confirm(`Delete testimonial from ${rev.author}?`)) return;
    await http.delete(`/admin/reviews/${rev.review_id}`);
    refresh();
  }

  return (
    <div className="space-y-8" data-testid="admin-reviews-page">
      <div>
        <div className="flex items-center gap-3">
          <MessageSquareQuote className="w-6 h-6 text-gold" />
          <h1 className="font-serif text-4xl text-navy">Testimonials</h1>
        </div>
        <p className="mt-3 text-navy/65 max-w-2xl leading-relaxed">
          Real client reviews. These appear on the home page and (when tagged to a service)
          on the matching service detail page. They also power your Google rich-result
          star ratings via JSON-LD AggregateRating schema.
        </p>
      </div>

      <NewReviewForm onSaved={refresh} />

      <div>
        <h2 className="text-xs uppercase tracking-[0.25em] text-navy/55 mb-4">
          All testimonials ({items.length})
        </h2>
        {loading ? (
          <div className="flex items-center gap-2 text-navy/60">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="card p-6 text-sm text-navy/55">
            No testimonials yet. Add your first above — once you have 5+, they start
            showing as star ratings in Google search results.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((r) => (
              <div key={r.review_id} className="card p-5" data-testid={`review-row-${r.review_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-serif text-lg text-navy">{r.author}</div>
                    {r.location && (
                      <div className="text-xs text-navy/55">{r.location}</div>
                    )}
                  </div>
                  <Stars value={r.rating} />
                </div>
                <p className="mt-3 text-sm text-navy/75 leading-relaxed italic">
                  &ldquo;{r.body}&rdquo;
                </p>
                {r.service_slug && (
                  <div className="mt-3 text-[10px] uppercase tracking-widest text-gold">
                    Tagged: {r.service_slug}
                  </div>
                )}
                <div className="mt-4 pt-3 border-t border-navy/5 flex items-center justify-between text-xs">
                  <button
                    onClick={() => toggle(r)}
                    data-testid={`toggle-${r.review_id}`}
                    className={`flex items-center gap-1 ${r.active ? "text-teal" : "text-navy/40"}`}
                  >
                    {r.active ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
                    {r.active ? "Visible" : "Hidden"}
                  </button>
                  <button
                    onClick={() => remove(r)}
                    data-testid={`delete-${r.review_id}`}
                    className="text-red-500 hover:text-red-600 flex items-center gap-1"
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
